import { ReactElement, useState, useEffect, ChangeEvent, MouseEvent } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  ClickAwayListener,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  Popper,
  Table,
  Toolbar,
  Typography,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import MuiListItemIcon from '@mui/material/ListItemIcon';
import { styled } from '@mui/material/styles';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { headerHeight } from 'components/Header/Header';
import { AppBaseButton } from 'components/AppBaseButton/AppBaseButton';
import { Application, Calculation, CalculationStatus, CalculationType, SupportWidth } from 'types/domainTypes';
import { WorstPerformingCombinationsView } from './WorstPerformingCombinationsView';
import { MinimumReactionView } from './MinimumReactionView';
import { StatusChip } from 'components/CriteriaResultsRow/CriteriaResultsRow';
import { saveAs } from 'file-saver';
import { getAppConfig } from 'config/config';
import { CalculationResultsPanelInfo } from './CalculationResultsPanelInfo';
import { prepareAuthHeaders } from 'config/authConfig';
import { useSnackbar } from 'notistack';
import { MinimumWidthView } from './MinimumWidthView';
import { useAppSelector } from 'redux/hooks';
import ZoneResult from 'components/molecules/ZoneResult/ZoneResult';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CheckboxGroupItem } from '../../components/molecules/CheckboxGroup/CheckboxGroup';
import Checkbox from '../../components/atoms/Checkbox/Checkbox';
import { CalculationContext } from './CalculationContextProvider';

export const ResultsPanelTable = styled(Table)(() => ({
  '& th, td': { padding: 0 },
}));

const IconDrawer = styled(MuiDrawer)(({ theme }) => ({
  backgroundColor: 'transparent',
  width: `calc(${theme.spacing(7)} + 1px)`,
  overflowX: 'hidden',
  '& .MuiDrawer-paper': {
    backgroundColor: '#F4F5F7',
    width: `calc(${theme.spacing(7)} + 1px)`,
    overflowX: 'hidden',
    paddingTop: `${headerHeight}px`,
    zIndex: 3,
  },
}));

const PanelDrawer = styled(MuiDrawer)(({ theme }) => ({
  width: `0px`,
  zIndex: theme.zIndex.drawer + 2,
  '& .MuiDrawer-paper': {
    width: `460px`,
  },
}));

const ListItemIcon = styled(MuiListItemIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  ':hover': {
    color: theme.palette.primary.dark,
  },
}));

const HeaderBar = styled(Toolbar)(() => ({
  '&': { paddingRight: '2px', paddingLeft: '16px' },
  position: 'fixed',
  zIndex: 1,
  backgroundColor: 'white',
  minHeight: '64px',
  width: '460px',
  borderBottom: '1px solid #d3d3d3',
}));

const FooterBar = styled(HeaderBar)(() => ({
  '&': { padding: '0px 20px' },
  '& button': { width: '100%' },
  borderBottom: '0px',
  borderTop: '1px solid #d3d3d3',
  bottom: 0,
}));

const PopperMenu = styled(Popper)(() => ({
  padding: '10px 20px',
  borderRadius: '4px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
  backgroundColor: 'white',
}));

interface CalculationResultsPanelProps {
  calculation: Calculation;
  projectId: string;
  supportWidth: SupportWidth;
  onRecalculate: () => void;
  panelOpen: boolean;
  handleToggle: () => void;
  handleCancelCalculation?: () => void;
  isUpdatingResult: boolean;
}

export function CalculationResultsPanel({
  calculation,
  projectId,
  supportWidth,
  onRecalculate,
  panelOpen,
  handleToggle,
  handleCancelCalculation,
  isUpdatingResult,
}: CalculationResultsPanelProps): ReactElement {
  const { t } = useTranslation();
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const sectionStates = { worstPerformingCombinations: false, minimumWidth: false, minimumReaction: false };
  const [expanded, setExpanded] = useState({ ...sectionStates, worstPerformingCombinations: true });
  const [includeLoadDiagrams, setIncludeLoadDiagrams] = useState(localStorage.getItem('include-load-diagrams') === 'true');
  const [includeCalculationDiagrams, setIncludeCalculationDiagrams] = useState(
    localStorage.getItem('include-calculation-diagrams') === 'true'
  );
  useEffect(() => {
    localStorage.setItem('include-load-diagrams', String(includeLoadDiagrams));
    localStorage.setItem('include-calculation-diagrams', String(includeCalculationDiagrams));
  }, [includeLoadDiagrams, includeCalculationDiagrams]);
  const diagramCheckboxItems: CheckboxGroupItem<string>[] = [
    {
      label: `${t('calculation-page.results-panel.include-diagram-options.load-diagrams')}`,
      value: 'loadDiagrams',
    },
    {
      label: `${t('calculation-page.results-panel.include-diagram-options.calculation-result-diagrams')}`,
      value: 'calculationDiagrams',
    },
  ];
  const handleDiagramOptionsChange = (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (e.target.value === 'loadDiagrams') {
      setIncludeLoadDiagrams(checked);
    } else if (e.target.value === 'calculationDiagrams') {
      setIncludeCalculationDiagrams(checked);
    }
  };
  const handleSectionClick = (section: keyof typeof sectionStates) => {
    setExpanded({ ...sectionStates, [section]: !expanded[section] });
  };

  const {
    result,
    resultExpired,
    status,
    details: { type },
  } = calculation;
  const isFastenerCheck = type === CalculationType.FastenerCheck;
  const hasResult =
    result &&
    ((type === CalculationType.PanelCheck &&
      result.criteria &&
      result.criteria.length > 0 &&
      result.supports &&
      result.supports.length > 0) ||
      (isFastenerCheck && result.zones && result.zones.length > 0));
  const displayResults = !isUpdatingResult && hasResult && status !== CalculationStatus.Calculating;
  const isAllCriteriaPass = !!result?.criteria.every((criteria) => criteria.isPass);
  const isFastenerPass = isFastenerCheck && calculation.status === CalculationStatus.Completed;
  const division = useAppSelector((state) => state.user.division);
  const [isDiagramPopperOpen, setIsDiagramPopperOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = (e: MouseEvent<HTMLElement>) => {
    setIsDiagramPopperOpen(!isDiagramPopperOpen);
    setAnchorEl(e.currentTarget);
  };

  useEffect(() => {
    console.log('Entering calculation results panel with calculation: ', calculation);
  }, [calculation]);

  const clickAwayHandler = () => {
    setIsDiagramPopperOpen(false);
  };
  const downloadReport = async () => {
    setIsPdfDownloading(true);
    const requestHeaders: HeadersInit = new Headers();
    requestHeaders.set('Content-Type', 'application/json');

    try {
      const res = await fetch(
        `${getAppConfig().apiUrl}/report/${projectId}?timezone=${
          Intl.DateTimeFormat().resolvedOptions().timeZone
        }&division=${division}&showLoadDiagrams=${includeLoadDiagrams}&showCalculationDiagrams=${includeCalculationDiagrams}`,
        {
          method: 'POST',
          headers: await prepareAuthHeaders(requestHeaders),
          body: JSON.stringify([calculation.id]),
        }
      );

      if (res.ok) {
        const blob = await res.blob();
        saveAs(blob, `${calculation.details.name}.pdf`);
      } else {
        enqueueSnackbar(t('calculation-page.results-panel.pdf-generation-error', { status: `${res.status} ${res.statusText}` }), {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar(t('calculation-page.results-panel.pdf-download-error'), {
        variant: 'error',
      });
    }

    setIsPdfDownloading(false);
  };

  return (
    <>
      <IconDrawer variant="permanent" anchor="right" open={false}>
        <List>
          <ListItemButton onClick={handleToggle}>
            <ListItemIcon data-testid="calculation-result-panel-button">
              <AssignmentTurnedInIcon />
            </ListItemIcon>
          </ListItemButton>
        </List>
      </IconDrawer>
      <PanelDrawer anchor="right" open={panelOpen} hideBackdrop ModalProps={{ disableScrollLock: true }}>
        <HeaderBar variant="dense">
          <Grid container justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
            <Grid item xs={8}>
              <Typography variant="h6" fontFamily="KingspanBrownTT-Bold">
                {t('calculation-page.results-panel.title')}
              </Typography>
            </Grid>
            <Grid item xs={2} paddingLeft="14px">
              {displayResults && (
                <StatusChip
                  data-testid="overall-result-chip"
                  isPass={isAllCriteriaPass || isFastenerPass}
                  label={
                    <Typography variant="caption">
                      {isFastenerCheck
                        ? isFastenerPass
                          ? t('calculation.status-completed')
                          : t('calculation.status-fail')
                        : isAllCriteriaPass
                        ? t('calculation.status-pass')
                        : t('calculation.status-fail')}
                    </Typography>
                  }
                />
              )}
            </Grid>
            <Grid item xs={2} paddingLeft="25px">
              <IconButton onClick={handleToggle} data-testid="calculation-result-panel-close-button">
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </HeaderBar>
        <Box sx={{ minHeight: '63px', zIndex: 0 }} />

        {resultExpired && status !== CalculationStatus.Calculating && displayResults && (
          <>
            <Alert severity="warning" sx={{ margin: '15px', paddingBottom: '0px' }}>
              <AlertTitle>
                <strong>{t('calculation-page.results-panel.expired-result-title')}</strong>
              </AlertTitle>
              {t('calculation-page.results-panel.expired-result-message')}
              <div style={{ textAlign: 'right', marginTop: '20px', marginBottom: '5px' }}>
                <AppBaseButton variant="contained" onClick={onRecalculate} data-testid={'reCalculate'} sx={{}}>
                  {t('calculation-page.results-panel.expired-result-button-recalculate')}
                </AppBaseButton>
              </div>
            </Alert>
            <Divider />
          </>
        )}

        {(status === CalculationStatus.Calculating || isUpdatingResult) && (
          <CalculationResultsPanelInfo
            title={t('calculation-page.results-panel.calculating-title', {
              type: isFastenerCheck ? t('calculation-page.results-panel.fastener-type') : t('calculation-page.results-panel.panel-type'),
            })}
            infoText={t('calculation-page.results-panel.calculating-message')}
            showSpinner
            handleCancel={handleCancelCalculation}
          />
        )}

        {displayResults && (
          <>
            {!isFastenerCheck ? (
              <>
                <WorstPerformingCombinationsView
                  calculationId={result.id}
                  criteria={result.criteria}
                  expanded={expanded.worstPerformingCombinations}
                  onClick={() => handleSectionClick('worstPerformingCombinations')}
                />
                <MinimumWidthView
                  results={result.supports}
                  expanded={expanded.minimumWidth}
                  supportWidth={supportWidth}
                  onClick={() => handleSectionClick('minimumWidth')}
                />
                <MinimumReactionView
                  result={result}
                  expanded={expanded.minimumReaction}
                  onClick={() => handleSectionClick('minimumReaction')}
                />
              </>
            ) : (
              <ZoneResult results={result.zones || []} />
            )}
            <Box sx={{ minHeight: '63px', zIndex: 0 }} />
            <FooterBar>
              <AppBaseButton
                variant="contained"
                onClick={downloadReport}
                disabled={calculation.resultExpired || isPdfDownloading}
                isLoading={isPdfDownloading}
              >
                {t('calculation-page.results-panel.export-report-button')}
              </AppBaseButton>
              {!isFastenerCheck && (
                <Box sx={{ paddingLeft: '10px' }}>
                  <div>
                    <IconButton
                      data-testid={'testId'}
                      aria-label="more"
                      id="more-button"
                      aria-controls={isDiagramPopperOpen ? 'menu' : undefined}
                      aria-expanded={isDiagramPopperOpen ? 'true' : undefined}
                      aria-haspopup="true"
                      onClick={handleClick}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <PopperMenu
                      id={'calculation-results-select-diagrams-popper'}
                      open={isDiagramPopperOpen}
                      anchorEl={anchorEl}
                      modifiers={[
                        {
                          name: 'offset',
                          options: {
                            offset: [-190, 20],
                          },
                        },
                      ]}
                      style={{ zIndex: 10000 }}
                    >
                      <ClickAwayListener onClickAway={clickAwayHandler}>
                        <Box>
                          {diagramCheckboxItems.map((option, index) => (
                            <Checkbox
                              key={`diagram-option-${index}`}
                              label={option.label}
                              value={option.value}
                              checked={option.value === 'loadDiagrams' ? includeLoadDiagrams : includeCalculationDiagrams}
                              onChange={handleDiagramOptionsChange}
                            />
                          ))}
                        </Box>
                      </ClickAwayListener>
                    </PopperMenu>
                  </div>
                </Box>
              )}
            </FooterBar>
          </>
        )}

        {!hasResult && status !== CalculationStatus.Calculating && (
          <CalculationResultsPanelInfo
            title={t('calculation-page.results-panel.no-results-title')}
            infoText={
              isFastenerCheck
                ? t('calculation-page.results-panel.no-fastener-results-message')
                : t('calculation-page.results-panel.no-results-message')
            }
          />
        )}
      </PanelDrawer>
    </>
  );
}
